import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import classes from './Transactions.module.css';
import invoicePreviewClasses from '../Invoice Preview/Reportinvoice.module.css';
import HeaderNav from '../../Components/Dahboard Header/HeaderNav/HeaderNav';
import Footer from "../../Components/Footer/Footer";
import { useLocation, useNavigate } from 'react-router-dom';
// use centralized API wrapper
import api, { withCompanyId } from '../../Components/API/Api';
import { Pencil, Trash2, Printer } from 'lucide-react';
// BASE_URL is provided by API module when needed
import { useUser } from '../../UserContext';
import searchicon from "../../Assets/searchicon.png";
import filter from "../../Assets/filter.png";
import Swal from 'sweetalert2';
import { useReactToPrint } from "react-to-print";
import ActionPage from "../ActionPage/ActionPage";
import { QRCodeSVG } from "qrcode.react";

export const filterTransactions = (invoices = [], searchTerm = '', statusFilter = '') => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const normalizedStatus = statusFilter.trim().toLowerCase();

    return invoices.filter((invoice) => {
        const searchableValues = [
            invoice.invoice_number,
            invoice.uuid,
            invoice.customer?.name,
            invoice.customer_id,
            invoice.description,
            Array.isArray(invoice.sales)
                ? invoice.sales
                    .map((sale) => sale?.item?.name || sale?.item?.description || sale?.item_id)
                    .filter(Boolean)
                    .join(' ')
                : '',
            invoice.transaction_date,
        ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

        const matchesSearch = !normalizedSearch || searchableValues.includes(normalizedSearch);
        const matchesStatus = !normalizedStatus || String(invoice.status || '').trim().toLowerCase() === normalizedStatus;

        return matchesSearch && matchesStatus;
    });
};

const Transactions = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [invoiceItems, setInvoiceItems] = useState([]);
    const [companyInfo, setCompanyInfo] = useState(null);
    const [metrics, setMetrics] = useState({
        total_sales: 0,
        outstanding: 0,
        total_invoices: 0,
        // overdue: 0, 
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [previewInvoice, setPreviewInvoice] = useState(null);
    const [editingInvoice, setEditingInvoice] = useState(null);
    const printRef = useRef(null);

    const pageMap = {
        '/dashboard': 'Dashboard',
        '/transactions': 'Transactions',
        '/accounts': 'Accounting',
        '/reports': 'Reports',
        '/banking': 'Banking',
        '/settings': 'Settings',
    };


    const { user } = useUser();

    const filteredInvoiceItems = useMemo(() => {
        return filterTransactions(invoiceItems, searchTerm, statusFilter);
    }, [invoiceItems, searchTerm, statusFilter]);

    const getPreviewItems = (invoice) => {
        const collections = [invoice?.sales, invoice?.items, invoice?.invoice_items, invoice?.line_items, invoice?.sales_invoice_items];
        const selected = collections.find(Array.isArray);
        if (selected) return selected;

        const arrayFields = ['item_id', 'quantity', 'item_amount', 'reference'];
        const length = Math.max(...arrayFields.map((field) => Array.isArray(invoice?.[field]) ? invoice[field].length : 0), 0);
        return Array.from({ length }, (_, index) => ({
            id: index + 1,
            item_id: invoice?.item_id?.[index],
            quantity: invoice?.quantity?.[index],
            item_amount: invoice?.item_amount?.[index],
            reference: invoice?.reference?.[index],
            amount: invoice?.amount,
        }));
    };

    const formatInvoiceAmount = (value) => Number(value || 0).toLocaleString('en-NG', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const previewItems = previewInvoice ? getPreviewItems(previewInvoice) : [];
    const computedItemTotal = previewItems.reduce(
        (sum, item) => sum + Number(item.item_amount ?? item.amount ?? item.total ?? item.line_total ?? 0),
        0,
    );
    const previewSubtotal = Number(
        previewInvoice?.sub_total ??
        previewInvoice?.subtotal ??
        (computedItemTotal || Number(previewInvoice?.amount || 0))
    );
    const previewVat = Number(previewInvoice?.total_vat ?? previewInvoice?.vat_amount ?? 0);
    const previewDiscount = Number(previewInvoice?.total_discount ?? previewInvoice?.discount_amount ?? 0);
    const previewTotal = Number(previewInvoice?.amount ?? previewInvoice?.total_price ?? previewInvoice?.total ?? previewInvoice?.balance ?? previewSubtotal);
    const qrCodeValue = previewInvoice ? JSON.stringify({
        type: "Sales Invoice",
        invoiceNumber: previewInvoice.invoice_number || previewInvoice.uuid || "",
        customer: previewInvoice.customer?.name || previewInvoice.customer_id || "",
        date: previewInvoice.transaction_date || "",
        dueDate: previewInvoice.due_date || "",
        amount: previewTotal,
    }) : "";
    const companyBankName = companyInfo?.bank_name || previewInvoice?.company?.bank_name || "—";
    const companyAccountName =
        companyInfo?.account_name ||
        previewInvoice?.company?.account_name ||
        companyInfo?.name ||
        previewInvoice?.company?.name ||
        "—";
    const companyAccountNumber = companyInfo?.account_number || previewInvoice?.company?.account_number || "—";
    const companyLogo =
        companyInfo?.logo ||
        previewInvoice?.company?.logo ||
        previewInvoice?.business?.logo ||
        previewInvoice?.organization?.logo ||
        previewInvoice?.company_logo ||
        "/promix.png";

    const handleOpenFullPreview = () => {
        if (!previewInvoice) return;
        navigate('/reportinvoice', {
            state: { invoice: previewInvoice, invoiceType: 'Sales Invoice' },
        });
    };

    const printPreview = useReactToPrint({
        contentRef: printRef,
        documentTitle: `Sales-Invoice-${previewInvoice?.invoice_number || previewInvoice?.uuid || ""}`,
        pageStyle: `
            @page { margin: 12mm; }
            html, body { margin: 0; background: #fff; color: #222; }
            #transactions-invoice-modal-print * { visibility: visible !important; }
            #transactions-invoice-modal-print {
                display: block !important;
                box-sizing: border-box !important;
                padding: 10mm 8mm !important;
            }
            #transactions-invoice-modal-print .${invoicePreviewClasses.header} {
                display: flex !important;
                flex-direction: row !important;
                align-items: flex-start !important;
                justify-content: space-between !important;
                gap: 20px !important;
            }
            #transactions-invoice-modal-print .${invoicePreviewClasses.company} {
                display: flex !important;
                flex-direction: row !important;
                align-items: center !important;
                flex: 1 1 auto !important;
            }
            #transactions-invoice-modal-print .${invoicePreviewClasses["invoice-title"]} {
                display: block !important;
                flex: 0 0 auto !important;
                margin-left: auto !important;
                text-align: right !important;
            }
            #transactions-invoice-modal-print .${invoicePreviewClasses.items} th {
                color: #fff !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
            }
            .${classes.printPreviewOverlay}, .${classes.printPreviewHeader}, .${classes.printPreviewActions} { display: none !important; }
            .${invoicePreviewClasses["invoice-box"]} { box-shadow: none !important; border: 0 !important; }
        `,
    });

    const refreshInvoices = useCallback(async () => {
        try {
            const [response, metricsRes] = await Promise.all([
                api.get(`/v1/fetch-sales-invoice`),
                api.get(`/v1/metrics/sales-invoice-metrics`),
            ]);
            setInvoiceItems(response.data?.data || []);
            if (metricsRes.data?.data) {
                setMetrics(metricsRes.data.data);
            }
        } catch (error) {
            console.error("Failed to fetch invoices/metrics:", error);
        }
    }, []);

    useEffect(() => {
        const fetchInvoices = async () => {
            setLoading(true);
            await refreshInvoices();
            setLoading(false);
        };

        fetchInvoices();
    }, [user, refreshInvoices]);

    useEffect(() => {
        const fetchCompanyInfo = async () => {
            if (!previewInvoice || companyInfo) return;

            try {
                const response = await api.get('/v1/companies/get-info');
                const company = Array.isArray(response?.data?.data)
                    ? response.data.data[0]
                    : response?.data?.data;
                if (company) {
                    setCompanyInfo(company);
                }
            } catch (error) {
                console.error("Failed to fetch company details for invoice preview:", error);
            }
        };

        fetchCompanyInfo();
    }, [previewInvoice, companyInfo]);

    const handleInvoiceSaved = async () => {
        setEditingInvoice(null);
        await refreshInvoices();
    };

    const handleReverseInvoice = async (item) => {
        // Confirm deletion
        const result = await Swal.fire({
            title: 'Reverse Sales Invoice',
            text: 'Are you sure you want to reverse this sales invoice? This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, reverse it!'
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            const response = await api.post(`/v1/reverse_sales_invoice`, withCompanyId({ 
                invoice_id: item.id,
                invoice_number: item.invoice_number
            }));
            
            if (response.status === 200 || response.status === 201) {
                setInvoiceItems((currentItems) => currentItems.filter((invoiceItem) => invoiceItem.id !== item.id));
                Swal.fire({
                    title: 'Success!',
                    text: 'Sales invoice reversed successfully.',
                    icon: 'success',
                    confirmButtonColor: '#3085d6'
                });
            }
        } catch (error) {
            console.error("Failed to reverse invoice:", error);
            Swal.fire({
                title: 'Error!',
                text: error.response?.data?.message?.invoice_number?.[0] || 'Error reversing sales invoice. Please try again.',
                icon: 'error',
                confirmButtonColor: '#3085d6'
            });
        }
    };

    return (
        <div>
            <HeaderNav activePage={pageMap[location.pathname]} />

            <div className={classes.Container}>

                <div className={classes.Container}>
                    <div className={classes.actions}>
                        <button className={classes.btns}>New Sales Invoice</button>
                        <button className={classes.btns}>New Purchase Invoice</button>
                        <button className={classes.btns}>All Invoices</button>
                        <button className={`${classes.btns} ${classes.greens}`}>Product & Services</button>
                    </div>

                    <header className={classes.Header}>
                        <h2 className={classes.Title}>Sales Invoice</h2>
                        <h1 className={classes.Titles}>
                            Manage your sales transactions, invoices, and customer payments.
                        </h1>
                    </header>

                    <div className={classes.entrjj}>
                        <div className={classes.allkads}>
                            <div className={classes.thrdkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Total invoices</p>
                                    <p className={classes.kdptg1}>
                                        ₦{Number(metrics.total_sales).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                                    </p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>This month</p>
                                    </div>
                                </div>
                            </div>
                            <div className={classes.frtkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Outstanding</p>
                                    <p className={classes.kdptg2}>
                                        ₦{Number(metrics.outstanding).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                                    </p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Pending payments</p>
                                    </div>
                                </div>
                            </div>
                            {/* <div className={classes.fiftkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Overdue</p>
                                    <p className={classes.kdptg3}>
                                        ₦{Number(metrics.overdue || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                                    </p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Past due dates</p>
                                    </div>
                                </div>
                            </div> */}
                            <div className={classes.frstkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>This month</p>
                                    <p className={classes.kdptg4}>{metrics.total_invoices}</p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Total invoices</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>


                    <div className={classes.headerActions}>
                        <div className={classes.actionsRight}>
                            <div className={classes.inputWrapper}>
                                <img src={searchicon} alt="Search" className={classes.inputIconGreen} />
                                <input
                                    type="text"
                                    className={classes.searchBar}
                                    placeholder="Search sales"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className={classes.selectWrapper}>
                                <select
                                    className={classes.dropdown}
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="">All Status</option>
                                    <option value="Paid">Paid</option>
                                    <option value="Pending">Pending</option>
                                    <option value="Overdue">Overdue</option>
                                    <option value="Draft">Draft</option>
                                </select>
                                <span className={classes.dropdownArrow}>▼</span>
                            </div>
                            <div className={classes.inputWrapper}>
                                <input type="text" className={classes.searchBars} placeholder="Filter..." />
                                <img src={filter} alt="Filter" className={classes.inputIcon} />
                            </div>
                        </div>
                        <button className={classes.btnss} onClick={() => navigate('/transactions/newaction')}> + New items</button>
                    </div>
                    {/* <div className={classes.action}>
                        <button className={classes.btn} onClick={() => navigate('/transactions/newaction')}>
                            + Action
                        </button>
                    </div> */}
                    <div className={classes.invoicecont}>
                        <table className={classes.invoiceTable}>
                            <thead>
                                <tr>
                                    <th>S/N</th>
                                    <th>Transaction Date</th>
                                    <th>Invoice Number</th>
                                    <th>Customer</th>
                                    <th>Description</th>
                                    <th>Total Amount</th>
                                    {/* <th>Total Paid</th> */}
                                    {/* <th>Balance</th> */}
                                    {/* <th>Status</th> */}
                                    <th>Post Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    [...Array(5)].map((_, i) => (
                                        <tr key={i}>
                                            {[...Array(8)].map((__, j) => (
                                                <td key={j}><div className={classes.skeleton}></div></td>
                                            ))}
                                        </tr>
                                    ))
                                ) : filteredInvoiceItems.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>
                                            No sales invoices found.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredInvoiceItems.map((item, index) => (
                                        <tr key={item.id || index}>
                                            <td>{index + 1}</td>
                                            <td>{item.transaction_date || "—"}</td>
                                            <td>{item.invoice_number || item.uuid || "—"}</td>
                                            <td style={{textAlign: 'left'}}>
                                                <span className={classes.truncateName}>
                                                    {item.customer?.name || item.customer_id || "—"}
                                                </span>
                                            </td>


                                            <td style={{textAlign: 'left'}}>
                                                {item.description || '—'}
                                            </td>
                                            <td style={{textAlign:'right'}}>{Number(item.amount || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}</td>
                                            {/* {(() => {
                                                const toNum = (v) => {
                                                    const n = parseFloat(v);
                                                    return isNaN(n) ? 0 : n;
                                                };

                                                // Sum all debits from customer's ledgers (or item.ledgers fallback)
                                                const ledgers = Array.isArray(item.customer?.ledgers)
                                                    ? item.customer.ledgers
                                                    : (Array.isArray(item.ledgers) ? item.ledgers : []);

                                                const totalPaidFromLedgers = ledgers.reduce((sum, l) => sum + toNum(l?.debit), 0);

                                                // If no ledgers, fall back to invoice-level debit if provided
                                                const totalPaid = totalPaidFromLedgers > 0 ? totalPaidFromLedgers : toNum(item.debit);

                                                const balance = Math.max(0, toNum(item.amount) - totalPaid);

                                                return (
                                                    <>
                                                        <td style={{textAlign:'right'}}>{Number(totalPaid || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}</td>
                                                        <td style={{textAlign:'right'}}>{Number(balance || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}</td>
                                                    </>
                                                );
                                            })()} */}

                                            {/* <td>
                                                {(() => {
                                                    const toNum = (v) => {
                                                        const n = parseFloat(v);
                                                        return isNaN(n) ? 0 : n;
                                                    };

                                                    // Prefer invoice-level fields if present
                                                    const debitNum = toNum(item.debit);
                                                    const creditNum = toNum(item.credit);
                                                    const amountNum = toNum(item.amount);

                                                    if (!isNaN(creditNum)) {
                                                        // Rule 1: no credit amount => Owing
                                                        if (creditNum === 0) {
                                                            return <span className={classes.statusPending}>Owing</span>;
                                                        }
                                                        // Rule 3: debit zero => Paid
                                                        if (debitNum === 0) {
                                                            return <span className={classes.statusPaid}>Paid</span>;
                                                        }
                                                        // Rule 2: debit not equal to amount => Paid Part
                                                        if (amountNum > 0 && debitNum !== amountNum) {
                                                            return <span className={(classes.statusWarning || classes.statusPending)}>Paid Part</span>;
                                                        }
                                                        // Default for other combos
                                                        return <span className={classes.statusPending}>Owing</span>;
                                                    }

                                                    // Fallback to ledgers aggregation when invoice fields absent
                                                    const ledgers = Array.isArray(item.customer?.ledgers)
                                                        ? item.customer.ledgers
                                                        : (Array.isArray(item.ledgers) ? item.ledgers : []);

                                                    if (ledgers.length > 0) {
                                                        const totals = ledgers.reduce((acc, l) => ({
                                                            debit: acc.debit + toNum(l.debit),
                                                            credit: acc.credit + toNum(l.credit)
                                                        }), { debit: 0, credit: 0 });

                                                        const totalDebit = totals.debit;
                                                        const totalCredit = totals.credit;
                                                        const outstanding = Math.max(0, totalDebit - totalCredit);

                                                        if (totalCredit === 0) {
                                                            return <span className={classes.statusPending}>Owing</span>;
                                                        }
                                                        if (totalDebit === 0 || outstanding === 0) {
                                                            return <span className={classes.statusPaid}>Paid</span>;
                                                        }
                                                        if (totalDebit !== (amountNum || totalDebit)) {
                                                            return <span className={(classes.statusWarning || classes.statusPending)}>Paid Part</span>;
                                                        }
                                                        return <span className={classes.statusPending}>Owing</span>;
                                                    }

                                                    // Last resort: balance
                                                    const bal = parseFloat(item.balance);
                                                    const paid = !isNaN(bal) && bal === 0;
                                                    return (
                                                        <span className={paid ? classes.statusPaid : classes.statusPending}>
                                                            {paid ? 'Paid' : 'Pending'}
                                                        </span>
                                                    );
                                                })()}
                                            </td> */}
                                            <td>{item.created_at?.split("T")[0] || "—"}</td>
                                            <td className={classes.actionsIcons}>
                                                <span
                                                    onClick={() => setEditingInvoice(item)}
                                                    style={{ cursor: 'pointer' }}
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label={`Edit invoice ${item.invoice_number || item.uuid || ''}`}
                                                    onKeyDown={(event) => {
                                                        if (event.key === 'Enter' || event.key === ' ') {
                                                            event.preventDefault();
                                                            setEditingInvoice(item);
                                                        }
                                                    }}
                                                >
                                                    <Pencil size={16} style={{ color: 'rgb(22, 163, 74)' }} />
                                                </span>
                                                <Trash2 
                                                    size={16} 
                                                    style={{ color: 'rgb(255, 2, 2)', margin: '0 6px', cursor: 'pointer' }} 
                                                    onClick={() => handleReverseInvoice(item)}
                                                />
                                                <span
                                                    onClick={() => setPreviewInvoice(item)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <Printer size={16} style={{ color: 'rgb(37,99,235)' }} />
                                                </span>

                                            </td>

                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>

                        <div className={classes.paginationContainer}>
                            <div className={classes.paginationControls}>
                                <button>First</button>
                                <button className={classes.active}>1</button>
                                <button>2</button>
                                <button>Last</button>
                                <span className={classes.pageText}>
                                    Displaying 1 - {filteredInvoiceItems.length} of {filteredInvoiceItems.length}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>


            </div>
            <Footer />
            {editingInvoice && (
                <div
                    className={classes.editModalOverlay}
                    role="presentation"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) setEditingInvoice(null);
                    }}
                    onKeyDown={(event) => {
                        if (event.key === 'Escape') setEditingInvoice(null);
                    }}
                >
                    <section
                        className={classes.editModal}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="transactions-edit-title"
                    >
                        <header className={classes.editModalHeader}>
                            <h2 id="transactions-edit-title">Edit Sales Invoice</h2>
                            <button
                                type="button"
                                className={classes.printPreviewClose}
                                onClick={() => setEditingInvoice(null)}
                                aria-label="Close edit form"
                            >
                                ×
                            </button>
                        </header>
                        <div className={classes.editModalBody}>
                            <ActionPage
                                invoice={editingInvoice}
                                embedded
                                onClose={() => setEditingInvoice(null)}
                                onSaved={handleInvoiceSaved}
                            />
                        </div>
                    </section>
                </div>
            )}
            {previewInvoice && (
                <div
                    className={classes.printPreviewOverlay}
                    role="presentation"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) setPreviewInvoice(null);
                    }}
                    onKeyDown={(event) => {
                        if (event.key === 'Escape') setPreviewInvoice(null);
                    }}
                >
                    <section
                        className={classes.printPreviewModal}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="transactions-invoice-preview-title"
                    >
                        <header className={classes.printPreviewHeader}>
                            <h2 id="transactions-invoice-preview-title">Sales Invoice Preview</h2>
                            <button
                                type="button"
                                className={classes.printPreviewClose}
                                onClick={() => setPreviewInvoice(null)}
                                aria-label="Close preview"
                            >
                                ×
                            </button>
                        </header>
                        <div className={classes.printPreviewBody}>
                            <article id="transactions-invoice-modal-print" className={invoicePreviewClasses["invoice-box"]} ref={printRef}>
                                <div className={invoicePreviewClasses.watermark}>{previewInvoice?.status === 'Paid' ? 'PAID' : 'UNPAID'}</div>
                                <div className={invoicePreviewClasses.header}>
                                    <div className={invoicePreviewClasses.company}>
                                        <img
                                            src={companyLogo}
                                            alt="Company Logo"
                                            className={invoicePreviewClasses.companyLogo}
                                            onError={(event) => {
                                                event.currentTarget.onerror = null;
                                                event.currentTarget.src = "/promix.png";
                                            }}
                                        />
                                        <div>
                                            <h2>Promix Accounting Ltd</h2>
                                            <small>
                                                123 Business District Victoria Island, Lagos
                                                <br />
                                                +234 801 234 5678 | info@promix.com
                                            </small>
                                        </div>
                                    </div>
                                    <div className={invoicePreviewClasses["invoice-title"]}>
                                        <h1>Sales Invoice</h1>
                                        <p>
                                            Invoice No: {previewInvoice.invoice_number || previewInvoice.uuid || '—'}
                                            <br />
                                            Date: {previewInvoice.transaction_date || '—'}
                                            <br />
                                            Due Date: {previewInvoice.due_date || '—'}
                                        </p>
                                    </div>
                                </div>
                                <table className={invoicePreviewClasses.info}>
                                    <tbody>
                                        <tr>
                                            <td>
                                                <strong>BILL TO</strong>
                                                <br />
                                                {previewInvoice.customer?.name || previewInvoice.customer_id || '—'}
                                                <br />
                                                {previewInvoice.customer?.address || '123 Business District Victoria Island, Lagos'}
                                                <br />
                                                {previewInvoice.customer?.phone || '+234 801 234 5678'}
                                            </td>
                                            <td>
                                                <strong>Payment Terms</strong>
                                                <br />
                                                {previewInvoice.payment_terms || 'Due on receipt'}
                                            </td>
                                            <td className={classes.invoiceQrCell}>
                                                <div className={classes.invoiceQrCode}>
                                                    <QRCodeSVG value={qrCodeValue} size={96} level="M" includeMargin />
                                                    <span>Invoice details</span>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <table className={invoicePreviewClasses.items}>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Description</th>
                                            <th>Qty</th>
                                            <th>Unit Price (₦)</th>
                                            <th>Amount (₦)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewItems.length > 0 ? previewItems.map((item, index) => {
                                            const quantity = Number(item.quantity ?? item.qty ?? 1);
                                            const lineAmount = Number(item.item_amount ?? item.line_total ?? item.total ?? item.amount ?? 0);
                                            const unitPrice = Number(item.unit_price ?? item.price ?? item.rate ?? (quantity ? lineAmount / quantity : lineAmount));
                                            const title = item.item?.name || item.product?.name || item.name || item.reference || item.description || '—';
                                            return (
                                                <tr key={item.id ?? index}>
                                                    <td>{index + 1}</td>
                                                    <td>{title}</td>
                                                    <td>{quantity}</td>
                                                    <td>{formatInvoiceAmount(unitPrice)}</td>
                                                    <td>{formatInvoiceAmount(lineAmount)}</td>
                                                </tr>
                                            );
                                        }) : (
                                            <tr>
                                                <td>1</td>
                                                <td>{previewInvoice.description || 'Sales invoice'}</td>
                                                <td>1</td>
                                                <td>{formatInvoiceAmount(previewTotal)}</td>
                                                <td>{formatInvoiceAmount(previewTotal)}</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                                <div className={invoicePreviewClasses.totals}>
                                    <table>
                                        <tbody>
                                            <tr>
                                                <td>Subtotal</td>
                                                <td style={{ textAlign: 'right' }}>{formatInvoiceAmount(previewSubtotal)}</td>
                                            </tr>
                                            {previewVat !== 0 && (
                                                <tr>
                                                    <td>VAT (7.5%)</td>
                                                    <td style={{ textAlign: 'right' }}>{formatInvoiceAmount(previewVat)}</td>
                                                </tr>
                                            )}
                                            {previewDiscount !== 0 && (
                                                <tr>
                                                    <td>Discount</td>
                                                    <td style={{ textAlign: 'right' }}>- {formatInvoiceAmount(previewDiscount)}</td>
                                                </tr>
                                            )}
                                            <tr>
                                                <td>Total</td>
                                                <td style={{ textAlign: 'right' }}>{formatInvoiceAmount(previewTotal)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div className={invoicePreviewClasses.clear} />
                                <div className={invoicePreviewClasses.section}>
                                    <strong>Payment Details</strong>
                                    <br />
                                    {companyBankName}
                                    <br />
                                    {companyAccountName}
                                    <br />
                                    {companyAccountNumber}
                                </div>
                                <div className={invoicePreviewClasses.section}>
                                    <strong>Notes</strong>
                                    <br />
                                    Please make payment on or before the due date.
                                    <br />
                                    Thank you for your business.
                                </div>
                                <div className={invoicePreviewClasses.signature}>
                                    ___________________________
                                    <br />
                                    Authorized Signature
                                </div>
                                <div className={invoicePreviewClasses.printFooter}>
                                    Created by
                                    <br />
                                    <a
                                        href="https://promixaccounting.com"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        https://promixaccounting.com
                                    </a>
                                </div>
                            </article>
                        </div>
                        <footer className={classes.printPreviewActions}>
                            <button type="button" className={classes.printPreviewSecondary} onClick={() => setPreviewInvoice(null)}>
                                Close
                            </button>
                            <button type="button" className={classes.printPreviewSecondary} onClick={printPreview}>
                                Print
                            </button>
                            <button type="button" className={classes.printPreviewPrimary} onClick={handleOpenFullPreview}>
                                Open full preview
                            </button>
                        </footer>
                    </section>
                </div>
            )}
        </div>
    );
};

export default Transactions;
